import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Shield, ArrowRight, Copy, Check } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

const ENDS_AT = new Date("2026-06-02T04:00:00Z").getTime();

const offers = [
  {
    code: "MEMORIAL15",
    percent: "15%",
    title: "Memorial Day Sale",
    description: "Site-wide savings on every full, interior, and exterior detail. Book by Mon June 1.",
    icon: Star,
    badge: "Limited time",
  },
  {
    code: "HEROES20",
    percent: "20%",
    title: "Military & Veteran",
    description: "Our thank-you to those who served. Active duty, reservists, and veterans — show ID at service.",
    icon: Shield,
    badge: "All month",
  },
];

const MemorialDayPromo = () => {
  const [copied, setCopied] = useState<string | null>(null);

  if (Date.now() >= ENDS_AT + 6 * 86_400_000) return null; // hide a few days after it ends

  const copy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(code);
      toast.success(`${code} copied — apply at checkout`);
      setTimeout(() => setCopied(null), 1800);
    } catch {
      toast.error("Couldn't copy — try long-pressing the code");
    }
  };

  return (
    <section
      aria-labelledby="memorial-day-heading"
      className="relative overflow-hidden py-20 md:py-28"
    >
      {/* Layered cinematic background — deep navy → black → deep maroon, with gold orbs */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 15% 20%, hsl(220 55% 11%) 0%, transparent 55%), radial-gradient(ellipse at 85% 80%, hsl(0 55% 12%) 0%, transparent 55%), hsl(0 0% 4%)",
        }}
      />
      <div className="absolute inset-0 bg-grid opacity-[0.06]" />
      {/* Stars */}
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 8% 20%, hsl(var(--gold)/0.7) 0.6px, transparent 1.2px), radial-gradient(circle at 24% 65%, hsl(var(--gold)/0.6) 0.6px, transparent 1.2px), radial-gradient(circle at 42% 30%, hsl(var(--gold)/0.7) 0.6px, transparent 1.2px), radial-gradient(circle at 58% 80%, hsl(var(--gold)/0.6) 0.6px, transparent 1.2px), radial-gradient(circle at 74% 18%, hsl(var(--gold)/0.7) 0.6px, transparent 1.2px), radial-gradient(circle at 88% 55%, hsl(var(--gold)/0.6) 0.6px, transparent 1.2px)",
          backgroundSize: "320px 220px",
        }}
      />
      <div className="orb-glow orb-gold w-[520px] h-[520px] -top-32 -left-24 opacity-20 orb-pulse" />
      <div className="orb-glow orb-gold w-[420px] h-[420px] -bottom-20 -right-20 opacity-15 orb-pulse" />

      <div className="relative container-luxury px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-12 md:mb-16"
        >
          <div className="inline-flex items-center gap-2 glass-ultra rounded-full px-4 py-2 mb-6">
            <Star size={12} className="text-primary fill-primary" />
            <span className="text-[10px] font-semibold tracking-[0.24em] uppercase text-primary">
              Memorial Day Weekend
            </span>
            <Star size={12} className="text-primary fill-primary" />
          </div>
          <h2
            id="memorial-day-heading"
            className="font-display text-3xl md:text-5xl lg:text-6xl font-bold leading-[1.05] mb-5"
          >
            Honor the day.{" "}
            <span className="gold-gradient-text-animated text-glow-strong">Save on the shine.</span>
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            We&apos;re honoring those who served with our biggest savings of the spring. Two
            stackable offers — one for everyone, one for our heroes.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-5 md:gap-6 max-w-4xl mx-auto">
          {offers.map((o, i) => (
            <motion.div
              key={o.code}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: i * 0.1 }}
              whileHover={{ y: -4 }}
              className="group relative overflow-hidden rounded-2xl border border-primary/20 bg-card/40 backdrop-blur-xl p-6 md:p-8 hover:border-primary/50 transition-colors"
            >
              {/* Top accent line */}
              <div
                className="absolute top-0 left-0 right-0 h-px"
                style={{
                  background:
                    "linear-gradient(90deg, transparent, hsl(var(--gold) / 0.6), transparent)",
                }}
              />
              {/* Corner glow */}
              <div
                className="absolute -top-20 -right-20 w-48 h-48 rounded-full opacity-30 group-hover:opacity-50 transition-opacity"
                style={{
                  background:
                    "radial-gradient(circle, hsl(var(--gold) / 0.35) 0%, transparent 70%)",
                }}
              />

              <div className="relative flex items-start justify-between mb-5">
                <div className="w-12 h-12 rounded-xl gold-gradient-bg flex items-center justify-center shadow-lg shadow-primary/20">
                  <o.icon size={20} className="text-primary-foreground" />
                </div>
                <span className="text-[9px] font-semibold tracking-[0.22em] uppercase text-primary/80 glass-ultra rounded-full px-2.5 py-1">
                  {o.badge}
                </span>
              </div>

              <div className="relative">
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="font-display text-5xl md:text-6xl font-bold gold-gradient-text-animated leading-none">
                    {o.percent}
                  </span>
                  <span className="text-muted-foreground text-sm uppercase tracking-[0.2em]">off</span>
                </div>
                <h3 className="font-display text-xl md:text-2xl font-bold mb-2">{o.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                  {o.description}
                </p>

                <button
                  onClick={() => copy(o.code)}
                  className="w-full mb-3 inline-flex items-center justify-between gap-2 px-4 py-3 rounded-xl border border-dashed border-primary/40 bg-primary/5 hover:bg-primary/10 hover:border-primary/70 transition-all group/code"
                  aria-label={`Copy promo code ${o.code}`}
                >
                  <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    Promo Code
                  </span>
                  <span className="font-mono font-bold tracking-[0.15em] text-primary text-sm md:text-base">
                    {o.code}
                  </span>
                  {copied === o.code ? (
                    <Check size={15} className="text-primary" />
                  ) : (
                    <Copy size={15} className="text-muted-foreground group-hover/code:text-primary transition-colors" />
                  )}
                </button>

                <Link
                  to={`/book?promo=${o.code}`}
                  className="w-full gold-gradient-bg text-primary-foreground px-5 py-3 text-sm font-semibold tracking-wide rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-primary/30 inline-flex items-center justify-center gap-2 btn-luxury"
                >
                  Book & Apply Code
                  <ArrowRight size={15} />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Tribute line */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="text-center text-xs md:text-sm text-muted-foreground/80 mt-10 max-w-xl mx-auto italic"
        >
          In remembrance of the men and women who gave everything for our freedom. Thank you.
        </motion.p>
      </div>
    </section>
  );
};

export default MemorialDayPromo;
